import useTranslation from 'next-translate/useTranslation';
import Head from 'next/head';
import { LinkProps } from 'next/link';
import { useRouter } from 'next/router';
import { FunctionComponent, useEffect } from 'react';

import useLoggedUser from '../../../../../hooks/use-logged-user';
import usePageViewEvent from '../../../../../hooks/use-page-view-event';
import useHreflang from '../../../../../utils/seo/hreflang';
import useOGMeta from '../../../../../utils/seo/ogmeta';
import useSchema from '../../../../../utils/seo/schema-org/schema';

const VersionsSchema: FunctionComponent<{
  selection: any;
  info: any;
  vehicle: any;
  stock: TResultAPIStocksItem[];
  breadcrumbs: Array<{
    href: LinkProps | LinkProps['href'];
    as: LinkProps['as'];
    text: string;
  }>;
  translatedSlugs: any;
}> = ({ selection, info, vehicle, breadcrumbs, translatedSlugs, stock }) => {
  const { locale = 'en', asPath, pathname, query } = useRouter();
  const localePath = locale !== 'en' ? `/${locale}` : '';
  const { t } = useTranslation();
  const user = useLoggedUser();
  const subCategoryPlural = `parts-and-categories-plural:parts.${selection?.category.category}.${info?.subCategoryEnPart}`;

  const name = selection
    ? t('versions:head.with-part.title', {
        part: info?.subCategoryEnPart
          ? t(
              subCategoryPlural,
              {},
              {
                fallback: info?.subCategory,
              }
            )
          : info?.subCategory,
        vehicle: `${vehicle.brand.name} ${vehicle.model.name}`,
        year: `[${vehicle.model?.start_date}-${
          vehicle.model?.end_date || new Date().getFullYear()
        }]`,
        count: stock && stock[0]?.stock.toLocaleString(locale),
        yoursWord:
          info?.subCategoryType && info?.subCategoryType === 'F'
            ? t('grammar:pronnouns.yours.feminine')
            : t('grammar:pronnouns.yours.masculine'),
        usedWord:
          info?.subCategoryType && info?.subCategoryType === 'F'
            ? t('grammar:words.used.feminine')
            : t('grammar:words.used.masculine'),
      })
    : t('versions:head.no-part.title', {
        vehicle: `${vehicle.brand.name} ${vehicle.model.name}`,
        year: `[${vehicle.model?.start_date}-${
          vehicle.model?.end_date || new Date().getFullYear()
        }]`,
        count: stock && stock[0]?.stock.toLocaleString(locale),
      });

  const description = selection
    ? t('versions:head.with-part.description', {
        part: info?.subCategoryEnPart
          ? t(
              subCategoryPlural,
              {},
              {
                fallback: info?.subCategory,
              }
            )
          : info?.subCategory,
        vehicle: `${vehicle.brand.name} ${vehicle.model.name}`,
        year: `[${vehicle.model?.start_date}-${
          vehicle.model?.end_date || new Date().getFullYear()
        }]`,
        count: stock && stock[0]?.stock.toLocaleString(locale),
        yoursWord:
          info?.subCategoryType && info?.subCategoryType === 'F'
            ? t('grammar:pronnouns.yours.feminine')
            : t('grammar:pronnouns.yours.masculine'),
        usedWord:
          info?.subCategoryType && info?.subCategoryType === 'F'
            ? t('grammar:words.used.feminine')
            : t('grammar:words.used.masculine'),
      })
    : t('versions:head.no-part.description', {
        vehicle: `${vehicle.brand.name} ${vehicle.model.name}`,
        year: `[${vehicle.model?.start_date}-${
          vehicle.model?.end_date || new Date().getFullYear()
        }]`,
        count: stock && stock[0]?.stock.toLocaleString(locale),
      });

  usePageViewEvent(name);

  const alternateLinks = useHreflang({
    locale,
    route: pathname.includes('[subCategory]')
      ? 'versions-with-part'
      : 'versions',
    params: query,
    translatedSlugs: translatedSlugs?.slugs,
  });

  const og = useOGMeta({
    title: name,
    description,
    url: process.env.NEXT_PUBLIC_HOST + localePath + asPath,
    image: vehicle.brand.logo_url,
  });

  return (
    <Head>
      <title>{name}</title>
      <meta name="description" key="description" content={description} />
      {alternateLinks}
      {og}

      <script {...useSchema({ name, description, breadcrumbs })} />
    </Head>
  );
};

export default VersionsSchema;
